import { clone, mergeDeepWith, path } from 'ramda';
import validationMethods from 'js/utils/Validation/validationMethods';

const { email } = validationMethods;

export const basicValidation = {
    amount: {
        required: true,
        money: { error: 'playtech.mobile.system.mobile-cashier.amount.tooltip.format' },
        range: { error: 'playtech.mobile.system.mobile-cashier.amount.tooltip.range' }
    },
    accountId: {
        required: true
    },
    cvv2: {
        required: true,
        length: {
            min: 3,
            max: 4,
            error: 'playtech.mobile.system.mobile-cashier.error.cvv2.length'
        },
        regexp: {
            re: /^$|^\d+$/,
            error: 'playtech.mobile.system.mobile-cashier.error.cvv2.regexp'
        }
    }
};

export const accountIdEmailValidation = {
    required: true,
    fn: {
        fn: ({ accountId }) =>
            accountId === 'add' ? null : email(accountId)
    }
};

export const deposit = {
    generic: {
        validationConfig: {
            ...basicValidation,
            requiredPredefinedValue: {
                required: true
            }
        }
    },
    basic: {
        validationConfig: basicValidation
    },
    /* Non generic PMs with custom layer */
    DotpayCard: {},
    PayPalViaSC: {},
    QIWIviaSC: {},
    NETeller: {
        validationConfig: {
            ...basicValidation,
            accountPwd: {
                required: true,
                length: {
                    min: 6,
                    max: 6,
                    error: 'playtech.mobile.system.mobile-cashier.method.NETeller.accountPwd.length.tooltip'
                }
            },
            accountId: {
                ...basicValidation.accountId,
                regexp: {
                    re: /^$|(^[0-9]{12}$)|(^(?!^\.+)(?!.*\.+[^\w])(?=.*@.*\.)[\w._-]+@([0-9a-zA-Z-_]+\.){0,2}[a-zA-Z]{2,}$)/,
                    error: 'playtech.mobile.system.mobile-cashier.method.NETeller.accountId.format.tooltip'
                }
            }
        }
    },
    Ukash: {
        validationConfig: {
            ...basicValidation,
            accountId: {
                ...basicValidation.accountId,
                length: {
                    min: 19,
                    max: 19
                },
                number: {
                    // find out why `priority`
                    priority: 50,
                    re: /^\d+$/,
                    error: 'playtech.mobile.system.mobile-cashier.method.Ukash.accountCode.OnlyNumber.tooltip'
                }
            }
        }
    },
    PrePaidCard: {
        validationConfig: {
            ...basicValidation,
            accountId: {
                ...basicValidation.accountId,
                number: {
                    re: /^\d+$/,
                    error: 'playtech.mobile.system.mobile-cashier.method.Ukash.accountCode.OnlyNumber.tooltip'
                },
                regexp: {
                    re: /^\d{10}$/,
                    error: 'playtech.mobile.system.mobile-cashier.method.Ukash.accountCode.CardFieldLength.tooltip'
                }
            },
            accountPwd: {
                required: true,
                length: {
                    min: 4,
                    max: 4
                },
                number: {
                    re: /^$|^\d+$/,
                    error: 'playtech.mobile.system.mobile-cashier.method.Ukash.accountCode.OnlyNumber.tooltip'
                }
            }
        }

    },
    DotpayBlik: {
        validationConfig: {
            ...basicValidation,
            altAccount: {
                required: true
            },
            field4: {
                required: {
                    error: 'playtech.mobile.system.mobile-cashier.blik.regulations.error'
                }
            }
        }
    },
    DotpayBankTransfer: {
        isGeneric: false,
        validationConfig: {
            ...basicValidation,
            subMethodsCodes: {
                required: true
            },
            field4: {
                required: {
                    error: 'playtech.mobile.system.mobile-cashier.DotpayBankTransfer.regulations.error'
                }
            },
            field5: {
                required: {
                    error: 'playtech.mobile.system.mobile-cashier.DotpayBankTransfer.personaldata.error'
                }
            }
        }
    },
    VisaCheckout: {
        validationConfig: {
            amount: {
                required: true,
                money: {
                    error: 'playtech.mobile.system.mobile-cashier.amount.tooltip.format'
                },
                range: {
                    error: 'playtech.mobile.system.mobile-cashier.amount.tooltip.range'
                }
            },
            accountId: {
                required: true
            }
        }
    },
    /* --------------------------------- */
    PayPal: {
        validationConfig: {
            ...basicValidation,
            accountId: accountIdEmailValidation
        }
    },
    Moneybookers: {
        validationConfig: {
            ...basicValidation,
            accountId: accountIdEmailValidation
        }
    },
    WebMoney: {
        validationConfig: {
            ...basicValidation,
            accountId: {
                ...basicValidation.accountId,
                regexp: {
                    re: /^[A-Z][0-9]{12}$/,
                    error: 'playtech.mobile.system.mobile-cashier.method.WebMoney.accountId.format.tooltip'
                }
            }
        }
    },
    Envoy: {
        validationConfig: {
            ...basicValidation,
            amount: {
                ...basicValidation.amount,
                required: false
            }
        }
    }
};

export const withdraw = mergeDeepWith(
    clone,
    deposit,
    {
        Envoy: {
            validationConfig: {
                amount: {
                    required: true
                }
            }
        },
        EcoCard: {
            validationConfig: {
                ...basicValidation,
                accountId: {
                    ...basicValidation.accountId,
                    length: {
                        min: 1,
                        max: 60
                    }
                }
            }
        },
        IDS: {
            validationConfig: {
                ...basicValidation,
                accountId: {
                    ...basicValidation.accountId,
                    length: {
                        min: 1,
                        max: 30
                    },
                    regexp: {
                        re: /^\w+[-+ ]\w+[-+ ]\w+$/i,
                        error: 'playtech.mobile.system.mobile-cashier.method.IDS.accountId.regexp.tooltip'
                    }
                }
            }
        }
    }
);

export default ({ paymentType, code }) => path([paymentType, code], { deposit, withdraw });
